import { createDebug, type Debugger } from "../utils/LazyDebug";
import { CacheApi, CacheName, CacheStrategy } from "./CacheApi";

export interface RestApiParams {
  host: string;
  name: CacheName;
}

export class RestApi {
  #debug: Debugger;
  #cache: CacheApi;

  host: string;
  headers: HeadersInit & Record<string, string>;

  constructor(params: RestApiParams) {
    this.host = params.host;
    this.headers = {
      "content-type": "application/json",
    };
    this.#cache = new CacheApi({ name: params.name });
    this.#debug = createDebug(`wk:api:${params.name}`);
  }

  fetch<T>(params: FetchParams) {
    const { method = "GET", path, query, body } = params;

    const url = this.host + path + this.getQuerystring(query);
    const headers = {
      ...this.headers,
      ...params.headers,
    };

    const cacheKey = [method, url, body, headers];
    const cacheStrategy = params.cacheStrategy ?? CacheStrategy.None;

    return this.#cache.withCache<T>(cacheKey, cacheStrategy, async (signal) => {
      this.#debug("Fetch from remote", { method, url, headers });

      const response = await window.fetch(url, {
        method,
        headers,
        body,
        signal,
      });
      if (response.ok) {
        return response.json() as T;
      }
      throw Error(`Error ${response.status}`);
    });
  }

  getQuerystring(query?: Record<string, any>) {
    if (!query) {
      return "";
    }

    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((value) => params.append(key, value));
      } else if (value) {
        params.append(key, value);
      }
    });

    return "?" + params.toString();
  }

  get<T>({ path, query, headers, cacheStrategy }: GetParams<T>) {
    return this.fetch<T>({
      method: "GET",
      path,
      query,
      headers,
      cacheStrategy,
    });
  }

  post<T>({ path, query, body, headers }: PostParams) {
    return this.fetch<T>({
      method: "POST",
      path,
      query,
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  put<T>({ path, query, body, headers }: PutParams) {
    return this.fetch<T>({
      method: "PUT",
      path,
      query,
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  delete<T>({ path, query, body, headers }: DeleteParams) {
    return this.fetch<T>({
      method: "DELETE",
      path,
      query,
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  clearCache(force = false) {
    return this.#cache.clear(force);
  }
}

export interface FetchParams {
  path: string;
  method?: "GET" | "POST" | "DELETE" | "PUT";
  query?: Record<string, any>;
  body?: string;
  headers?: Record<string, any>;
  cacheStrategy?: CacheStrategy;
}

export interface GetParams<T> {
  path: string;
  query?: Record<string, any>;
  headers?: Record<string, any>;
  cacheStrategy?: CacheStrategy;
}

export interface PostParams {
  path: string;
  query?: Record<string, any>;
  body?: Object;
  headers?: Record<string, any>;
  cacheStrategy?: CacheStrategy;
}

export interface PutParams {
  path: string;
  query?: Record<string, any>;
  body?: Object;
  headers?: Record<string, any>;
}

export interface DeleteParams {
  path: string;
  query?: Record<string, any>;
  body?: Object;
  headers?: Record<string, any>;
}
